/**
 * Naming a repo's quantization scheme, so a refusal can teach instead of frustrate.
 *
 * Origin: an owner test-drive of a frontier-scale repo hit a `U8` tensor that
 * correctly refused to decode — `U8` is not castable to float in any meaningful
 * way. But the refusal was generic, and the *reason* was interesting: that repo
 * ships its bulk mass natively MXFP4-quantized, so those U8 bytes are a packed
 * quantized payload rather than integers we declined to convert. "Cannot decode
 * U8" is true and useless; "MXFP4 packed payload — decode not yet supported"
 * tells you what you are looking at and that the tool knows.
 *
 * This reads `config.json` — a small JSON file, one whole-file GET, fetched once
 * per repo and only when a decode has already failed. It never runs on the happy
 * path, so it costs the Hub nothing for the common case.
 */

import type {
  ArchitectureAnchors,
  DeclaredQuantization,
  QuantizationCandidate,
} from "../analysis/mlx.js";
import { ggmlTypeByName } from "../gguf/ggml-types.js";
import { type ResolvedRepo, fileUrl } from "./hub.js";

export interface QuantizationInfo {
  /** Human-readable scheme name, e.g. "MXFP4" or "MLX affine (4-bit, group 64)". */
  readonly label: string;
  /** Whether this app can currently decode it. */
  readonly supported: boolean;
  /**
   * Declared bits/group, when the config states them.
   *
   * Needed by the anatomy map to recover a quantized tensor's *logical* shape:
   * packed dims alone give one equation in two unknowns, so the config supplies
   * `bits` and the shapes then verify it. That verification is the fail-loud
   * cross-check — this is the "config" half of it, and it is deliberately
   * reported as declared rather than reconciled here.
   */
  readonly hint: { readonly bits: number; readonly groupSize: number } | undefined;
  /**
   * The same declaration in the form `findAffineGroups` consumes, with the
   * per-module entries MLX writes alongside the global ones.
   *
   * Kept separate from `hint` rather than folded into it: `hint` is the narrow
   * "both numbers present" shape the label and the anatomy map already use, and
   * per-module overrides only mean anything to the resolver.
   */
  readonly declared?: DeclaredQuantization | undefined;
  /**
   * `vocab_size` / `hidden_size` from the same file, when it states them.
   *
   * Not quantization metadata at all — architecture. They are here because they
   * arrive in the same single `config.json` GET and because they are what lets
   * the resolver pin an ambiguous triple's bits/group split by measurement
   * instead of by trusting the numbers two fields above.
   */
  readonly anchors?: ArchitectureAnchors | undefined;
}

/** Cached per repo+sha: the answer cannot change for an immutable commit. */
const cache = new Map<string, QuantizationInfo | undefined>();

/**
 * Read and name the repo's quantization scheme, or `undefined` if it declares none.
 *
 * Never throws: this exists to improve an error message, and an error while
 * improving an error message should not replace the original one.
 */
export async function describeQuantization(
  repo: ResolvedRepo,
): Promise<QuantizationInfo | undefined> {
  const key = `${repo.repoId}@${repo.sha}`;
  if (cache.has(key)) return cache.get(key);

  let info: QuantizationInfo | undefined;
  try {
    if (repo.files.includes("config.json")) {
      const response = await fetch(fileUrl(repo, "config.json"));
      if (response.ok) {
        info = interpret(await response.json());
      }
    }
  } catch {
    info = undefined;
  }
  cache.set(key, info);
  return info;
}

/**
 * Map a `config.json` onto a scheme name.
 *
 * Deliberately conservative: an unrecognized config yields a generic label
 * naming whatever the config called itself, rather than a guess. Claiming the
 * wrong scheme would be worse than claiming none — this text appears next to
 * numbers people are trying to reason about.
 */
export function interpret(config: unknown, depth = 2): QuantizationInfo | undefined {
  if (config === null || typeof config !== "object") return undefined;
  const root = config as Record<string, unknown>;

  // Two spellings in the wild: `quantization_config` (transformers) and
  // `quantization` (MLX and friends).
  const raw = (root.quantization_config ?? root.quantization) as unknown;
  if (raw === null || typeof raw !== "object") {
    /*
     * A multimodal config puts the block one level down.
     *
     * `moonshotai/Kimi-K3` declares its quantization inside `text_config`,
     * where the root holds only `architectures`, `text_config` and
     * `vision_config`. Looking only at the root, this function returned
     * `undefined` — so the repo read as unquantized, its packed bytes were
     * counted as weights, and the card said 8.30 bits per weight for a 4.49-bit
     * model.
     *
     * `parseArchitectureAnchors` has descended for exactly this reason since it
     * was written; this one never learned to, and the two are called together
     * on the same object. Same key list, same depth, so they cannot disagree
     * about where a config's real content lives.
     */
    if (depth <= 0) return undefined;
    for (const key of NESTED_CONFIG_KEYS) {
      const nested = interpret(root[key], depth - 1);
      if (nested) {
        // Anchors from the *outer* config too: a nested block describes the
        // scheme, while `hidden_size` and friends may sit at either level.
        return {
          ...nested,
          anchors: nested.anchors ?? parseArchitectureAnchors(config),
        };
      }
    }
    return undefined;
  }
  const q = raw as Record<string, unknown>;

  const method = String(
    q.quant_method ?? q.method ?? q.format ?? q.quantization_method ?? "",
  ).toLowerCase();
  /*
   * `format` as well as `method`, because a wrapper can hide the scheme.
   *
   * `moonshotai/Kimi-K3` declares `quant_method: "compressed-tensors"` with
   * `format: "mxfp4-pack-quantized"`. The `??` chain above stops at the first
   * key present, so `method` is the *wrapper's* name and every scheme test
   * below missed a 4-bit checkpoint entirely — we counted its packed bytes as
   * weights and told readers 8.30 bits per weight for a 4.49-bit model, on the
   * flagship repo and on its share card.
   *
   * compressed-tensors is a container, not a scheme. What it contains is in
   * `format`, so both are read.
   */
  const format = String(q.format ?? q.fmt ?? "").toLowerCase();
  const scheme = `${method} ${format}`.trim();

  const candidates = compressedTensorsWeights(q);
  // One agreeing candidate is a scheme-wide declaration and is used as one.
  // Two are not: see `compressedTensorsWeights` for why they travel separately
  // instead of being collapsed into whichever came first.
  const nested = candidates.length === 1 ? candidates[0] : undefined;
  const bits =
    typeof q.bits === "number"
      ? q.bits
      : typeof q.num_bits === "number"
        ? q.num_bits
        : nested?.bits;
  const groupSize = typeof q.group_size === "number" ? q.group_size : nested?.groupSize;
  // `scheme`, not `method`: for a wrapper like compressed-tensors the scheme
  // name lives in `format`, and everything downstream that gates on the name —
  // `block-scaled.ts`, `compressed-tensors.ts` — needs to see it.
  const declared = parseDeclaredQuantization(q, bits, groupSize, scheme, candidates);
  const anchors = parseArchitectureAnchors(config);

  // MLX affine is the one M3 targets, and it is the only scheme this app will
  // claim it can decode. Everything else is named honestly and refused.
  const looksMlxAffine =
    bits !== undefined &&
    groupSize !== undefined &&
    (method === "" || method === "affine");
  if (looksMlxAffine) {
    return {
      label: `MLX affine (${bits}-bit, group ${groupSize})`,
      supported: true,
      hint: { bits, groupSize },
      declared,
      anchors,
    };
  }

  if (scheme.includes("mxfp4")) {
    return {
      label: "MXFP4 packed payload",
      supported: false,
      hint: undefined,
      declared,
      anchors,
    };
  }
  if (method) {
    const detail = [
      bits === undefined ? undefined : `${bits}-bit`,
      groupSize === undefined ? undefined : `group ${groupSize}`,
    ]
      .filter(Boolean)
      .join(", ");
    return {
      label: detail ? `${method} (${detail})` : method,
      supported: false,
      // Still useful even for a scheme we can't decode: if bits and group are
      // declared, the anatomy map can recover logical shapes and cross-check
      // them, which is exactly the case that made this worth building.
      hint:
        bits !== undefined && groupSize !== undefined ? { bits, groupSize } : undefined,
      declared,
      anchors,
    };
  }
  return undefined;
}

/**
 * Keys inside a `quantization` block that describe the *scheme* rather than a
 * module.
 *
 * Everything else whose value is an object or `false` is read as a per-module
 * entry, which is how MLX writes them — module paths sit as siblings of
 * `bits`/`group_size` in the same block, so there is no container to key off.
 * The blocklist is belt-and-braces: a stray non-module key would still have to
 * be a dotted ancestor of a real tensor prefix to affect anything.
 */
const SCHEME_KEYS: ReadonlySet<string> = new Set([
  "bits",
  "group_size",
  "quant_method",
  "method",
  "format",
  "quantization_method",
  "fmt",
  "mode",
  "linear_class",
  "modules_to_not_convert",
  "skip_modules",
]);

/**
 * Read the quantization block as a declaration, per-module entries included.
 *
 * MLX records exceptions inside the block itself:
 *
 *     "quantization": {
 *       "group_size": 64, "bits": 4,
 *       "model.embed_tokens": {"group_size": 32, "bits": 8},
 *       "lm_head": false
 *     }
 *
 * because its own loader consults it path by path when rebuilding the quantized
 * modules. Both spellings appear: an object for "quantized, differently", and
 * `false` for "not quantized at all". `true` means "as declared globally", which
 * is what the absence of an entry already means, so it is dropped rather than
 * recorded — an empty class is not worth carrying.
 *
 * Values are taken only when they are numbers; a string `"4"` is left as
 * `undefined` rather than coerced, on the same principle as everything else
 * here.
 */
export function parseDeclaredQuantization(
  q: Record<string, unknown>,
  bits: number | undefined,
  groupSize: number | undefined,
  method?: string | undefined,
  candidates?: readonly QuantizationCandidate[] | undefined,
): DeclaredQuantization {
  const overrides: Record<string, DeclaredQuantization | null> = {};

  for (const [key, value] of Object.entries(q)) {
    if (SCHEME_KEYS.has(key)) continue;
    if (value === false || value === null) {
      overrides[key] = null;
      continue;
    }
    if (typeof value !== "object" || Array.isArray(value)) continue;
    const entry = value as Record<string, unknown>;
    const entryBits = typeof entry.bits === "number" ? entry.bits : undefined;
    const entryGroup =
      typeof entry.group_size === "number" ? entry.group_size : undefined;
    // An object with neither field says nothing about a scheme; it is far more
    // likely to be some unrelated nested block than a module entry.
    if (entryBits === undefined && entryGroup === undefined) continue;
    overrides[key] = { bits: entryBits, groupSize: entryGroup };
  }

  const hasOverrides = Object.keys(overrides).length > 0;
  return {
    bits,
    groupSize,
    method: method || undefined,
    overrides: hasOverrides ? overrides : undefined,
    // Only when the config says more than one thing. A single candidate is
    // already `bits`/`groupSize` above, and repeating it here would give every
    // consumer two places to read the same fact from.
    candidates: candidates && candidates.length > 1 ? candidates : undefined,
  };
}

/**
 * Nested blocks that describe the *language* model of a multimodal checkpoint.
 *
 * `vision_config` and friends are deliberately absent: their `hidden_size` is
 * the vision tower's width, and using it as the embedding table's logical width
 * would be a confident wrong answer rather than no answer.
 */
const NESTED_CONFIG_KEYS: readonly string[] = [
  "text_config",
  "llm_config",
  "language_config",
  "language_model_config",
  "decoder_config",
  "thinker_config",
];

/** `hidden_size` under the names the common architectures give it. */
const HIDDEN_SIZE_KEYS: readonly string[] = [
  "hidden_size",
  "n_embd",
  "d_model",
  "model_dim",
];

const VOCAB_SIZE_KEYS: readonly string[] = ["vocab_size", "n_vocab"];
/** Attention shape anchors — read for the shape resolver, not for MLX. */
const HEADS_KEYS: readonly string[] = ["num_attention_heads", "n_head", "n_heads"];
const Q_LORA_RANK_KEYS: readonly string[] = ["q_lora_rank"];
const KV_LORA_RANK_KEYS: readonly string[] = ["kv_lora_rank"];
const HEAD_DIM_KEYS: readonly string[] = [
  "head_dim",
  "attention_head_dim",
  "v_head_dim",
];

function positiveInt(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : undefined;
}

function firstOf(
  config: Record<string, unknown>,
  keys: readonly string[],
): number | undefined {
  for (const key of keys) {
    const value = positiveInt(config[key]);
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Pull `hidden_size` and `vocab_size` out of a `config.json`.
 *
 * These are architecture, not quantization, and that is precisely why they are
 * worth reading: an embedding table's logical width is `hidden_size` whatever
 * the repo claims about bits and groups, so it can settle a bits/group split
 * that the stored shapes leave ambiguous — without the config's own quantization
 * numbers being trusted at all. It costs no extra request; the file is already
 * being fetched.
 *
 * The root wins over any nested block, and only language-model blocks are
 * descended into (two levels, since `thinker_config.text_config` happens).
 * Absent or unparseable fields stay `undefined` — a wrong `hidden_size` would
 * not merely fail to resolve a triple, it would resolve it to the wrong scheme,
 * so every value is required to be a positive integer and nothing is inferred
 * from a plausible-looking neighbour.
 *
 * Note that `interpret` only surfaces these when the config also declares a
 * quantization block. A repo with MLX triples and no such block cannot be loaded
 * by MLX either, so it is not a case worth widening the contract for.
 */
export function parseArchitectureAnchors(
  config: unknown,
  depth = 2,
): ArchitectureAnchors | undefined {
  if (config === null || typeof config !== "object" || Array.isArray(config)) {
    return undefined;
  }
  const root = config as Record<string, unknown>;

  let hiddenSize = firstOf(root, HIDDEN_SIZE_KEYS);
  let vocabSize = firstOf(root, VOCAB_SIZE_KEYS);
  let heads = firstOf(root, HEADS_KEYS);
  let qLoraRank = firstOf(root, Q_LORA_RANK_KEYS);
  let kvLoraRank = firstOf(root, KV_LORA_RANK_KEYS);
  let headDim = firstOf(root, HEAD_DIM_KEYS);

  if (depth > 0) {
    for (const key of NESTED_CONFIG_KEYS) {
      if (hiddenSize !== undefined && vocabSize !== undefined) break;
      const nested = parseArchitectureAnchors(root[key], depth - 1);
      if (nested === undefined) continue;
      hiddenSize ??= nested.hiddenSize;
      vocabSize ??= nested.vocabSize;
      heads ??= nested.heads;
      qLoraRank ??= nested.qLoraRank;
      kvLoraRank ??= nested.kvLoraRank;
      headDim ??= nested.headDim;
    }
  }

  if (hiddenSize === undefined && vocabSize === undefined) return undefined;
  return { hiddenSize, vocabSize, heads, qLoraRank, kvLoraRank, headDim };
}

/**
 * Turn a refusal to decode into a sentence that says what we actually know.
 *
 * The shape/role/bytes are shown by the caller regardless — a refusal should
 * still leave you knowing what the tensor *is*.
 *
 * ## What was wrong with the old sentence
 *
 * It read: *"IQ3_XXS isn't a float type, so there is no distribution to plot.
 * If this repo is quantized, its packed payloads need a scheme-specific
 * decoder."* Three visitors pasted it back at us as a bug report within hours
 * of the report button shipping, and they were right to.
 *
 * **"isn't a float type"** is true of the storage dtype and useless as an
 * explanation — it reads as *this tensor is something odd*, a shape array or an
 * index, when it is the model's weights. And **"if this repo is quantized"**
 * hedges about something we hold the answer to: it is a GGUF, the part is named
 * `UD-IQ3_XXS`, and `ggml-types.ts` has the block layout. Saying "if" while
 * holding the evidence is the tool refusing to use what it has.
 *
 * So a known ggml type is now named, measured from its own block layout, and
 * the limitation is stated as ours rather than as a property of the tensor.
 */
export function explainUndecodable(
  dtype: string,
  quantization: QuantizationInfo | undefined,
  measured?: { readonly numel: number; readonly byteLength: number },
): string {
  const ggml = ggmlTypeByName(dtype);
  /*
   * `blockSize > 1` is what makes it a *packed* type, and the guard is not
   * defensive padding — `GGML_TYPES` also holds `I8`, `I16`, `I32`, `I64` and
   * the plain floats, all at block size 1. Without it an `I64` index array was
   * described as "a packed ggml quantization — 1 weight in 8 bytes", which is
   * the same crime as the sentence this function was rewritten to fix: stating
   * something confident and wrong about a tensor we understand perfectly well.
   * Caught by `quant-config.test.ts`, which already pinned the I64 wording.
   */
  if (ggml && ggml.blockSize > 1) {
    /*
     * Bits per weight derived from the layout rather than tabulated, so this
     * sentence cannot drift from `GGML_TYPES`. Two decimals because the
     * interesting IQ types land between 2 and 4 and the tenths are what
     * distinguish them.
     */
    const tableBpw = (ggml.typeSize * 8) / ggml.blockSize;

    /*
     * **The table names the type; the file states the width.** A ggml type id
     * can collide — `prism-ml/Ternary-Bonsai-27B-gguf` stores 498 tensors of id
     * **42** at 68 bytes per 256 elements (2.125 bpw) while upstream publishes
     * id 42 as `Q2_0` at `(64, 18)` = 2.25. `gguf/inventory.ts` already resolves
     * that in the file's favour, and *this sentence did not*: it read the table
     * and told a visitor "about 2.25 bits each" directly beneath a header
     * reading 2.13, computed from the same file's real bytes.
     *
     * Reported as `ffcf61ac`, whose visitor said only "I can't open type42".
     * The disclosure they were missing had already been built; what nobody
     * checked was whether it stated the right number for the file that provoked
     * it. Sixth instance of one defect: a figure correct in one source, printed
     * where another source is authoritative.
     *
     * **The comparison is asymmetric, because padding only runs one way.**
     * `byteLength` may include padding to the next tensor's alignment, which can
     * only make the measured rate *higher* than the true one — never lower. So a
     * file measuring meaningfully *below* the table has to be storing a
     * different format, while one measuring above it may just be a small tensor
     * against a 32-byte boundary. Only the first is claimed.
     */
    const measuredBpw =
      measured && measured.numel > 0 && measured.byteLength > 0
        ? (measured.byteLength * 8) / measured.numel
        : undefined;
    if (measuredBpw !== undefined && measuredBpw < tableBpw - 0.01) {
      return `${dtype} is a packed ggml quantization, and this file stores it at about ${measuredBpw.toFixed(2)} bits per weight — not the published ${dtype} layout of ${ggml.blockSize} weights in ${ggml.typeSize} bytes (${tableBpw.toFixed(2)} bits), so the type id is reused here for a different format. Reading the numbers back out needs a decoder written specifically for this scheme, and that one isn't built yet. Everything above — the shape, the role, the byte count — is read straight from the file and is exact.`;
    }

    const bpw = tableBpw.toFixed(2);
    return `${dtype} is a packed ggml quantization — ${ggml.blockSize} weights in ${ggml.typeSize} bytes, about ${bpw} bits each. Reading the numbers back out needs a decoder written specifically for this scheme, and that one isn't built yet. Everything above — the shape, the role, the byte count — is read straight from the file and is exact.`;
  }
  if (quantization && !quantization.supported) {
    return `${quantization.label} — stored as ${dtype}. This is a packed quantized payload rather than plain numbers, and decoding this scheme isn't supported yet.`;
  }
  return `${dtype} holds packed or non-numeric data rather than plain floats, so there is no distribution to plot.`;
}

/**
 * compressed-tensors states its scheme one level down, and may state several.
 *
 * ```json
 * "quantization_config": {
 *   "quant_method": "compressed-tensors",
 *   "format": "pack-quantized",
 *   "config_groups": {
 *     "group_0": { "targets": [...], "weights": { "num_bits": 4, "group_size": 128, ... } },
 *     "group_1": { "targets": [...], "weights": { "num_bits": 3, "group_size": 128, ... } }
 *   }
 * }
 * ```
 *
 * Read for `num_bits` and `group_size` only — everything else the scheme
 * declares is checked against shapes rather than trusted.
 *
 * **Every distinct pair is returned, not just an agreeing one.** This used to
 * refuse a config whose groups disagreed, on the sound principle that one
 * number would be a claim about tensors it does not describe. The refusal was
 * right and its consequence was wrong: mixed-precision repos are the norm for
 * this format now, and refusing left
 * `kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS` — 4-bit attention, 3-bit MLP —
 * unfolded at a flat 32.00 bits per weight, which is a much larger claim than
 * the one being avoided.
 *
 * The inversion is that a set is not a number and does not have to be collapsed
 * into one *here*. The scheme-level `bits`/`groupSize` still go `undefined`
 * unless the groups agree, so nothing downstream that wants a single declared
 * width is handed a made-up one; the alternatives travel separately, for a
 * folder to test against real shapes and keep the one that survives.
 *
 * `targets` is deliberately not read. It names the modules each group covers —
 * genuinely useful, and the natural tiebreak if a repo ever admits two
 * candidates on one tensor — but it is a list of paths and regexes, i.e. a
 * second name-matching implementation to keep honest, and no measured repo
 * needs it: all 401 packed pairs in the repo above resolve to exactly one
 * candidate from the shapes alone.
 */
export function compressedTensorsWeights(
  q: Record<string, unknown>,
): { bits: number; groupSize: number }[] {
  const groups = q.config_groups;
  if (groups === null || typeof groups !== "object") return [];

  const found: { bits: number; groupSize: number }[] = [];
  for (const group of Object.values(groups as Record<string, unknown>)) {
    if (group === null || typeof group !== "object") continue;
    const weights = (group as Record<string, unknown>).weights;
    if (weights === null || typeof weights !== "object") continue;
    const w = weights as Record<string, unknown>;
    const bits = typeof w.num_bits === "number" ? w.num_bits : undefined;
    const groupSize = typeof w.group_size === "number" ? w.group_size : undefined;
    if (bits === undefined || groupSize === undefined) continue;
    // Distinct pairs only: three config groups declaring two widths between
    // them is two candidates, and a duplicate would read as ambiguity later.
    if (found.some((f) => f.bits === bits && f.groupSize === groupSize)) continue;
    found.push({ bits, groupSize });
  }
  return found;
}
